import { useState, useEffect, useRef } from "react";
import {
  ArrowLeft, RefreshCw, Loader2, AlertCircle, BarChart2,
  TrendingUp, Users, Shield, Clock, Maximize2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import AnalyticsChart from "../components/charts/AnalyticsChart";

interface DashboardViewProps {
  dashboardId: number;
  onBack: () => void;
}

// Mock widget data for system dashboards
function getMockWidgets(dashboardName: string) {
  const base = [
    {
      id: 1, widgetType: "kpi", title: "Total Requests Today",
      config: { value: 142, change: "+12%", trend: "up", icon: "users" }
    },
    {
      id: 2, widgetType: "kpi", title: "Approval Rate",
      config: { value: "87%", change: "+3%", trend: "up", icon: "check" }
    },
    {
      id: 3, widgetType: "kpi", title: "Pending > 48h",
      config: { value: 7, change: "-2", trend: "down", icon: "clock" }
    },
    {
      id: 4, widgetType: "kpi", title: "Active Checkpoints",
      config: { value: 24, change: "0", trend: "neutral", icon: "shield" }
    },
  ];

  const charts: any[] = [];

  if (dashboardName?.includes("Executive") || dashboardName?.includes("Access")) {
    charts.push(
      {
        id: 10, widgetType: "report", title: "Request Volume — Last 30 Days",
        config: {
          chartType: "line",
          data: {
            xAxis: ["Jan 1", "Jan 5", "Jan 10", "Jan 15", "Jan 20", "Jan 25", "Jan 30"],
            series: [{ name: "Requests", data: [32, 45, 38, 60, 55, 72, 68] }]
          }
        }
      },
      {
        id: 11, widgetType: "report", title: "Approval Rate by Department",
        config: {
          chartType: "bar",
          data: {
            xAxis: ["IT", "HR", "Finance", "Operations", "Security"],
            series: [{ name: "Approval %", data: [92, 85, 78, 88, 95] }]
          }
        }
      }
    );
  }

  if (dashboardName?.includes("Checkpoint")) {
    charts.push(
      {
        id: 20, widgetType: "report", title: "Denial Trend — Last 90 Days",
        config: {
          chartType: "line",
          data: {
            xAxis: ["Oct", "Nov", "Dec"],
            series: [{ name: "Denials", data: [18, 24, 15] }]
          }
        }
      },
      {
        id: 21, widgetType: "report", title: "Entry Methods Distribution",
        config: {
          chartType: "donut",
          data: {
            series: [
              { name: "Badge", value: 68 },
              { name: "Manual", value: 22 },
              { name: "RFID", value: 10 }
            ]
          }
        }
      }
    );
  }

  if (dashboardName?.includes("Security")) {
    charts.push(
      {
        id: 30, widgetType: "report", title: "Alert Volume by Type",
        config: {
          chartType: "bar",
          data: {
            xAxis: ["Intrusion", "Tailgating", "Watchlist", "Unregistered", "Other"],
            series: [{ name: "Alerts", data: [12, 8, 5, 15, 3] }]
          }
        }
      },
      {
        id: 31, widgetType: "report", title: "Security Events Timeline",
        config: {
          chartType: "line",
          data: {
            xAxis: ["00:00", "04:00", "08:00", "12:00", "16:00", "20:00"],
            series: [{ name: "Events", data: [2, 1, 8, 15, 12, 6] }]
          }
        }
      }
    );
  }

  if (dashboardName?.includes("Card")) {
    charts.push(
      {
        id: 40, widgetType: "report", title: "Active Cards by Access Level",
        config: {
          chartType: "bar",
          data: {
            xAxis: ["Basic", "Standard", "Elevated", "Full"],
            series: [{ name: "Cards", data: [245, 180, 62, 18] }]
          }
        }
      },
      {
        id: 41, widgetType: "report", title: "Cards Expiring Soon",
        config: {
          chartType: "donut",
          data: {
            series: [
              { name: "Valid", value: 480 },
              { name: "Expiring 30d", value: 35 },
              { name: "Expired", value: 12 }
            ]
          }
        }
      }
    );
  }

  if (dashboardName?.includes("Knowledge")) {
    charts.push(
      {
        id: 50, widgetType: "report", title: "Policy Acknowledgement Rate",
        config: {
          chartType: "gauge",
          data: { value: 78, min: 0, max: 100 }
        }
      },
      {
        id: 51, widgetType: "report", title: "Content by Category",
        config: {
          chartType: "bar",
          data: {
            xAxis: ["Safety", "Security", "Compliance", "HR", "Technical"],
            series: [{ name: "Items", data: [24, 18, 32, 15, 9] }]
          }
        }
      }
    );
  }

  return [...base, ...charts];
}

export default function DashboardView({ dashboardId, onBack }: DashboardViewProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const { data: dashboard, isLoading, error, refetch } = trpc.analytics.dashboards.get.useQuery(
    { id: dashboardId },
    { refetchOnWindowFocus: false }
  );

  const isNOC = dashboard?.name === "NOC Big Screen";

  const handleRefresh = () => {
    refetch();
    setLastRefresh(new Date());
  };

  // Auto-refresh for NOC
  useEffect(() => {
    if (!isNOC) return;
    const interval = setInterval(handleRefresh, 30_000);
    return () => clearInterval(interval);
  }, [isNOC]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <AlertCircle className="w-8 h-8 text-destructive" />
        <p className="text-sm text-muted-foreground">Failed to load dashboard</p>
        <Button variant="outline" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>
    );
  }

  const widgets = getMockWidgets(dashboard.name);
  const kpiWidgets = widgets.filter((w) => w.widgetType === "kpi");
  const chartWidgets = widgets.filter((w) => w.widgetType === "report");

  return (
    <div className={`flex flex-col h-full ${isNOC && isFullscreen ? "fixed inset-0 z-50 bg-slate-950 text-white" : ""}`}>
      {/* Dashboard Header */}
      <div className={`flex items-center justify-between px-6 py-4 border-b ${isNOC && isFullscreen ? "bg-slate-900 border-slate-700" : "bg-card"}`}>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div className="h-5 w-px bg-border" />
          <div>
            <h2 className={`font-semibold text-base ${isNOC && isFullscreen ? "text-white" : "text-foreground"}`}>
              {dashboard.name}
            </h2>
            <p className={`text-xs ${isNOC && isFullscreen ? "text-slate-400" : "text-muted-foreground"}`}>
              Last updated: {lastRefresh.toLocaleTimeString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dashboard.isSystem && <Badge variant="secondary" className="text-xs">System</Badge>}
          {isNOC && <Badge variant="outline" className="text-xs border-orange-300 text-orange-600">Kiosk</Badge>}
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {isNOC && (
            <Button
              variant={isFullscreen ? "default" : "outline"}
              size="sm"
              onClick={() => setIsFullscreen(!isFullscreen)}
            >
              <Maximize2 className="w-4 h-4 mr-2" />
              {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            </Button>
          )}
        </div>
      </div>

      {/* Dashboard Content */}
      <div className={`flex-1 overflow-auto p-6 space-y-6 ${isNOC && isFullscreen ? "bg-slate-950" : ""}`}>
        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {kpiWidgets.map((widget) => (
            <KPICard key={widget.id} widget={widget} dark={isNOC && isFullscreen} />
          ))}
        </div>

        {/* Chart Widgets */}
        {chartWidgets.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {chartWidgets.map((widget) => (
              <div
                key={widget.id}
                className={`rounded-xl border p-5 ${isNOC && isFullscreen ? "bg-slate-900 border-slate-700" : "bg-card"}`}
              >
                <h3 className={`font-medium text-sm mb-4 ${isNOC && isFullscreen ? "text-white" : "text-foreground"}`}>
                  {widget.title}
                </h3>
                <AnalyticsChart
                  chartType={widget.config.chartType}
                  data={widget.config.data}
                  height={240}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KPICard({ widget, dark }: { widget: any; dark: boolean }) {
  const trendColor = widget.config.trend === "up"
    ? "text-green-600"
    : widget.config.trend === "down"
    ? "text-red-500"
    : "text-muted-foreground";

  return (
    <div className={`rounded-xl border p-4 ${dark ? "bg-slate-900 border-slate-700" : "bg-card"}`}>
      <p className={`text-xs font-medium mb-1 ${dark ? "text-slate-400" : "text-muted-foreground"}`}>
        {widget.title}
      </p>
      <p className={`text-2xl font-bold ${dark ? "text-white" : "text-foreground"}`}>
        {widget.config.value}
      </p>
      {widget.config.change !== "0" && (
        <p className={`text-xs mt-1 ${trendColor}`}>
          {widget.config.change} vs yesterday
        </p>
      )}
    </div>
  );
}
